import { keyframes, styled } from "goober";
import { navigate, routes } from "../lib/urls";
import { Button } from "./Button";
import { closeModalStack } from "./globalModalStack";
import { Card } from "./Settings/shared";
import { Text } from "./Text";

function openAccessMap(nodeId: string) {
  navigate(routes.monitoring.access, { node: nodeId, ...closeModalStack() });
}

const Panel = styled(Card)`
  position: relative;
  overflow: hidden;
  justify-content: center;
  height: 82px;
  padding: 0 24px;
  flex: none;
`;

const HeadingRow = styled("div")`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 8px;
`;

const HeadingText = styled("div")`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const MapButton = styled(Button)`
  &&& {
    background: var(--surface-primary);
    /* A border, not an outline — Chromium rounds outline-width up to 1px. */
    border: 0.5px solid var(--gray-200);
  }
  &&&:hover:not(:disabled) {
    background:
      linear-gradient(var(--alpha4), var(--alpha4)), var(--surface-primary);
  }
`;

const Art = styled("div")`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 1480px;
  height: 260px;
  transform: translate(-50%, -50%) rotate(-4deg) scale(0.46);
  pointer-events: none;
  mask-image: linear-gradient(
    to right,
    transparent 22%,
    black 56%,
    black 95%,
    transparent 100%
  );
`;

// -56 is a whole number of dash periods (6+8), so the loop restart is seamless.
const flow = keyframes`
  to {
    stroke-dashoffset: -56;
  }
`;

const Lines = styled("svg")`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;

  path {
    fill: none;
    stroke: var(--gray-400);
    stroke-width: 2;
    stroke-linecap: round;
  }
  path[data-flow] {
    animation: ${flow} 7.5s linear infinite;
  }
`;

const SkeletonChip = styled("div")`
  position: absolute;
  width: 208px;
  height: 48px;
  border-radius: 12px;
  background: var(--alpha4);
  box-shadow: 0 0 0 3px var(--surface-secondary);
`;

// The real map's geometry at full size — 208x48 cards on a 272px column pitch,
// curves from edgePath — scaled down by the Art transform. The row pitch is
// opened up from the real 60px so the rows stay distinct at this scale.
const COLS = [92, 364, 636, 908, 1180];
const ROWS = [46, 124, 202];
const SKELETON_CHIPS = [
  { left: COLS[0]!, top: ROWS[0]! },
  { left: COLS[0]!, top: ROWS[1]! },
  { left: COLS[0]!, top: ROWS[2]! },
  { left: COLS[1]!, top: ROWS[0]! },
  { left: COLS[1]!, top: ROWS[1]! },
  { left: COLS[2]!, top: ROWS[0]! },
  { left: COLS[2]!, top: ROWS[1]! },
  { left: COLS[2]!, top: ROWS[2]! },
  { left: COLS[3]!, top: ROWS[1]! },
  { left: COLS[4]!, top: ROWS[0]! },
  { left: COLS[4]!, top: ROWS[2]! },
];

export function AccessMapPanel({
  nodeId,
  noun,
  guard,
}: {
  nodeId: string;
  noun: string;
  guard?: (proceed: () => void) => void;
}) {
  const open = () => openAccessMap(nodeId);
  return (
    <Panel>
      <Art aria-hidden>
        <Lines viewBox="0 0 1480 260">
          <path
            d="M300 70 C332 70, 332 70, 364 70"
            strokeDasharray="6 8"
            data-flow
          />
          <path d="M300 70 C332 70, 332 148, 364 148" />
          <path d="M300 148 C332 148, 332 148, 364 148" />
          <path d="M300 226 C332 226, 332 148, 364 148" />
          <path d="M572 70 C604 70, 604 70, 636 70" />
          <path d="M572 70 C604 70, 604 148, 636 148" />
          <path d="M572 148 C604 148, 604 226, 636 226" />
          <path
            d="M844 70 C876 70, 876 148, 908 148"
            strokeDasharray="6 8"
            data-flow
          />
          <path
            d="M1116 148 C1148 148, 1148 226, 1180 226"
            strokeDasharray="6 8"
            data-flow
          />
          <path d="M844 226 C1012 226, 1012 226, 1180 226" />
        </Lines>
        {SKELETON_CHIPS.map((pos) => (
          <SkeletonChip key={`${pos.left}-${pos.top}`} style={pos} />
        ))}
      </Art>
      <HeadingRow>
        <HeadingText>
          <Text variant="text-regular-md">Access map</Text>
          <Text
            variant="text-small"
            color="var(--text-secondary)"
            style={{ maxWidth: 340 }}
          >
            See everything this {noun} has access to, and everything that can
            reach {noun === "member" ? "them" : "it"}, in one place.
          </Text>
        </HeadingText>
        <MapButton
          size="l"
          variant="secondary"
          icon="orbit"
          onClick={() => (guard ? guard(open) : open())}
        >
          View on access map
        </MapButton>
      </HeadingRow>
    </Panel>
  );
}
